import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Typography } from '@material-ui/core'
import { ADMIN_TENANT_GET } from 'components/api'
import { BackdropProgress } from 'components'
import { cacheTenantName } from '../tenantNameCache'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

const TenantContext = createContext(null)

export function TenantProvider({ tenantId, children }) {
    const [details, setDetails] = useState(null)
    const [initialLoading, setInitialLoading] = useState(true)
    const [reloading, setReloading] = useState(false)
    const [error, setError] = useState(null)

    const loadTenant = useCallback((isReload = false) => {
        if (isReload) {
            setReloading(true)
        } else {
            setInitialLoading(true)
        }
        setError(null)
        return ADMIN_TENANT_GET(tenantId)
            .then(tenant => {
                setDetails(tenant)
                const name = tenant?.tenant_details?.tenant_name || tenant?.tenant_name
                if (name) cacheTenantName(tenantId, name)
                return tenant
            })
            .catch(err => {
                setError(getApiErrorMessage(err, 'Failed to load tenant'))
                throw err
            })
            .finally(() => {
                setInitialLoading(false)
                setReloading(false)
            })
    }, [tenantId])

    const reload = useCallback(() => loadTenant(true), [loadTenant])

    useEffect(() => {
        loadTenant(false).catch(() => {})
    }, [loadTenant])

    if (initialLoading) return <BackdropProgress />

    if (error && !details) {
        return (
            <div className="superadmin-placeholder">
                Failed to load tenant. {error}
            </div>
        )
    }

    const tenantName = details?.tenant_details?.tenant_name || details?.tenant_name
    const logo = details?.tenant_details?.branding?.logo

    return (
        <TenantContext.Provider value={{ tenantId, details, tenantName, logo, reload, reloading, error, setError }}>
            {reloading && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                    Reloading tenant…
                </Typography>
            )}
            {children}
        </TenantContext.Provider>
    )
}

export function useTenant() {
    const ctx = useContext(TenantContext)
    if (!ctx) throw new Error('useTenant must be used within TenantProvider')
    return ctx
}
